import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {apiEndpoints, CommonConstant} from '../../app.constants';
import {Mention, MentionResponse} from '../../../models/mention';
import {BackendHttpService} from '../../core/backend-http.service';
import {Router} from '@angular/router';
import {Observable} from 'rxjs/Observable';
import * as Chains from 'c4s';
import {EventTrackerService, Page, UserActionType, ActionSource} from '../event-tracker/event-tracker.service';
import * as Sentry from '@sentry/browser';
import { ActivePage } from '../../../models/event-tracker';

@Injectable()
export class MentionService extends BackendHttpService<any> {

  constructor( protected router: Router,
               protected translate: TranslateService,
               private eventTracker: EventTrackerService
  ) {
    super(router, translate);
  }

  addNewProjectMention(mention: Mention) {
    return this.post(apiEndpoints.addNewProjectMention, mention);
  }

  deleteMention(mentionId) {
    const deleteUrl = apiEndpoints.deleteMention;
    return this.delete(`${deleteUrl}/${mentionId}`);
  }

  searchMention(searchCriteria) {
    return this.post(apiEndpoints.searchMention, searchCriteria).map(res => res.data);
  }

  /**
   * this method is use to find an existing mention by name
   * @param {string} mentionName
   * @returns {Observable<MentionResponse>}
   */
  findMentionByName(mentionName: string): Observable<MentionResponse> {
    return this.post(apiEndpoints.exists, {
      countries: [CommonConstant.INTERNATIONAL_ID],
      keyword: mentionName
    }).map(res => res.data.keyword);
  }

  /**
   * An action chain for new mention
   * - do before
   * - check exist
   * - add new mention if not existed and can create new
   * - do after
   * - track event to Segement
   * @param mention
   * @param beforeProcessing
   * @param afterProcessing
   * @param {ActivePage} activePage
   * @param {ActionSource} actionSource
   * @param {boolean} canCreate
   */
  processNewMention(mention, beforeProcessing?, afterProcessing?, activePage?: ActivePage, actionSource?: ActionSource, canCreate = true) {
    const state = {
      mentionService: this,
      eventTracker: this.eventTracker,
      beforeProcessing: beforeProcessing,
      afterProcessing: afterProcessing,
      mention: mention,
      persistedMention: null,
      hasError: false,
      activePage: activePage,
      actionSource: actionSource,
      canCreate: canCreate
    };

    // Run requests sequentially
    Chains(state)
      .then(this.doBeforeProcessingMention)
      .then(this.checkPostMentionExist)
      .then(this.addNewMention)
      .then(this.afterAddNewMentionSuccess)
      .catch((error) => {
        console.warn(error);
        // Report to Sentry
        let reported_error;

        if (error instanceof Error) {
          reported_error = error;
        } else if (error && error.message) {
          reported_error = new Error(error.message);
        } else {
          reported_error = new Error(error.toString());
        }

        Sentry.captureException(reported_error);
      });
  }

  private doBeforeProcessingMention(state, next) {
    if (state.beforeProcessing) {
      state.beforeProcessing(state.mention);
    }

    return next();
  }

  // Check if a mention exists
  private checkPostMentionExist(state, next) {
    state.mentionService.findMentionByName(state.mention.keyword).subscribe(
      // Success
      (data) => {
        state.mention_exists = !!data.id;
        state.persistedMention = data;

        return next();
      },

      // Error
      (error: Response | any) => {
        return next(error);
      }
    );
  }

  // Create a new mention
  private addNewMention(state, next) {
    // Mention already exists, skip this
    if (state.mention_exists || !state.canCreate) {
      return next();
    }

    // Make API call
    state.mentionService.addNewProjectMention(state.mention)
      .subscribe(resp => {
        state.persistedMention = resp.mention;
        return next();
      }, err => {
        state.hasError = true;
        return next();
      });
  }

  private afterAddNewMentionSuccess(state, next) {
    if (!state.hasError && !state.mention_exists && state.canCreate) {
      const newMention = state.persistedMention;
      // keep track event to Segment
      state.eventTracker.trackUserAction(UserActionType.ACTION, Page.MENTION, {
        id: newMention.id,
        name: newMention.keyword,
        type: newMention.keywordType.name
      }, {
        action: UserActionType.CREATE,
        source: state.actionSource || ActionSource.PAGE,
        ...newMention
      }, undefined, {
        type: state.activePage.type,
        name: state.activePage.name,
        id: state.activePage.id,
      });
    }

    if (!state.afterProcessing) {
      return next();
    }
    state.afterProcessing({
      hasError: state.hasError,
      alreadyExisted: state.mention_exists,
      newMention: state.persistedMention,
      errorMention: state.mention
    });
    return next();
  }

}



// WEBPACK FOOTER //
// ./src/app/services/mention/mention.service.ts